package com.steamstreet.vegasful.attribution

import kotlin.random.Random

/**
 * The single place an outbound Vegasful link acquires its campaign parameters.
 *
 * Every link we hand to somebody else — an email, a push deep link, a share, a store listing, a
 * tweet — goes out through [taggedUrl], so that the vocabulary lives in one file rather than in a
 * string concatenation at each call site. Nothing consumes these parameters yet; inbound capture
 * is a separate piece of work. What this file buys today is that the clicks arriving tomorrow are
 * recoverable at all.
 *
 * The scheme is standard UTMs, so GA4 and the ad platforms parse it without being taught anything,
 * plus three first-party parameters for what UTMs cannot express: which send (`vf_c`), which share
 * (`vf_s`), and which recipient (`vf_u`, always an opaque encrypted token and never a raw user id).
 *
 * Tagging must never break a link. Everything here is pure string work that cannot throw, and every
 * identifier is optional: a caller whose token generation failed passes null and the link still
 * goes out, merely less attributable.
 */

/** `utm_source` — the surface the link was minted on. */
public object LinkSource {
    /** Our own owned channels: the weekly email and push notifications. */
    public const val VEGASFUL: String = "vegasful"

    /** The public web site. */
    public const val WEB: String = "web"

    /** The iOS app. */
    public const val IOS: String = "ios"

    /** The Android app. */
    public const val ANDROID: String = "android"

    /** Twitter/X, as posted by the social publisher. */
    public const val TWITTER: String = "twitter"
}

/** `utm_medium` — the kind of channel, which is what channel-level reporting groups on. */
public object LinkMedium {
    public const val EMAIL: String = "email"
    public const val PUSH: String = "push"
    public const val SHARE: String = "share"
    public const val SOCIAL: String = "social"

    /** A link that leaves for an app store rather than for vegasful.com. */
    public const val APP_INSTALL: String = "app_install"
}

/**
 * `utm_campaign` — what the send was for.
 *
 * The push campaigns are exactly the notification categories named in `docs/retention.md`, because
 * that spec expects a `category` property on every send and every open; using the same strings for
 * both means the two never have to be mapped to one another.
 */
public object LinkCampaign {
    /** The weekly digest email. */
    public const val WEEKLY_EMAIL: String = "weekly"

    /**
     * The onboarding sequence sent to somebody who signed up by email and has never opened the app.
     * One category for all three messages: a person switching it off is telling us they do not want
     * to be onboarded, not that they object to the third message in particular.
     */
    public const val WELCOME: String = "welcome"

    /** Notification categories, per `docs/retention.md`. */
    public const val WEEKEND_GUIDE: String = "weekend_guide"
    public const val JUST_ANNOUNCED: String = "just_announced"
    public const val SAVED_EVENT_REMINDER: String = "saved_event_reminder"
    public const val BIG_EVENT: String = "big_event"

    /**
     * The single nudge for an event someone liked but never put on their calendar. Deliberately not
     * [SAVED_EVENT_REMINDER]: that reminds someone about a plan they already made, this one asks
     * them to make one. Whether the ask converts is the question the campaign exists to answer, and
     * folding the two together in reporting would make it unanswerable.
     */
    public const val LIKED_EVENT_REMINDER: String = "liked_event_reminder"

    /**
     * A push that reached the sender without one of the categories above. It should not happen
     * once every trigger names its category, and reads plainly in a report if it does.
     */
    public const val UNCATEGORIZED_PUSH: String = "push"

    /** A user sharing an entity from one of our surfaces. */
    public const val ENTITY_SHARE: String = "entity_share"

    /** AI-written new-event posts from the social publisher. */
    public const val AI_NEW_EVENTS: String = "ai_new_events"

    /** The mobile-web interstitial that offers the app. */
    public const val WEB_INTERSTITIAL: String = "web_interstitial"
}

/** The first-party parameter names, so nothing has to spell them out. */
public object LinkParameter {
    public const val UTM_SOURCE: String = "utm_source"
    public const val UTM_MEDIUM: String = "utm_medium"
    public const val UTM_CAMPAIGN: String = "utm_campaign"
    public const val UTM_CONTENT: String = "utm_content"
    public const val UTM_TERM: String = "utm_term"

    /** `vf_c` — the specific send or blast this link belongs to. */
    public const val SEND_ID: String = "vf_c"

    /** `vf_s` — the share that produced this link. */
    public const val SHARE_ID: String = "vf_s"

    /** `vf_u` — an opaque, encrypted recipient token. Never a raw user id. */
    public const val USER_TOKEN: String = "vf_u"
}

/**
 * The campaign identity of one outbound link.
 *
 * [source], [medium] and [campaign] are the three UTMs every link carries; the rest are present
 * only where they mean something. Values are emitted in the order declared here, which is the
 * order a person reading a link in an email would expect.
 *
 * @param content `utm_content` — which slot within the send produced the link, so two links from
 *   the same email can be told apart.
 * @param sendId `vf_c` — identifies the send itself. Shared by every link in one blast, which is
 *   what makes per-send dedup possible later.
 * @param shareId `vf_s` — identifies the individual share, so the viral loop is visible.
 * @param userToken `vf_u` — an encrypted recipient token. Pass null rather than anything readable.
 */
public class LinkTags(
    public val source: String,
    public val medium: String,
    public val campaign: String,
    public val content: String? = null,
    public val term: String? = null,
    public val sendId: String? = null,
    public val shareId: String? = null,
    public val userToken: String? = null
) {
    /** The parameters this tag set contributes, in emission order, unencoded. */
    public fun parameters(): List<Pair<String, String>> = listOfNotNull(
        LinkParameter.UTM_SOURCE to source,
        LinkParameter.UTM_MEDIUM to medium,
        LinkParameter.UTM_CAMPAIGN to campaign,
        content?.let { LinkParameter.UTM_CONTENT to it },
        term?.let { LinkParameter.UTM_TERM to it },
        sendId?.let { LinkParameter.SEND_ID to it },
        shareId?.let { LinkParameter.SHARE_ID to it },
        userToken?.let { LinkParameter.USER_TOKEN to it }
    ).filter { it.second.isNotEmpty() }

    /**
     * The tags as a query string with no leading `?`, each value encoded.
     *
     * Used where the parameters have to travel inside another parameter — the Play Store
     * `referrer` value is the one case — rather than as the query string of the link itself.
     */
    public fun queryString(): String = parameters().toQueryString()
}

/**
 * Attach [tags] to [url].
 *
 * Any query string [url] already carries is kept and the tags are appended to it, and a fragment
 * stays at the end where it belongs. The URL is returned unchanged when there is nothing to add.
 */
public fun taggedUrl(url: String, tags: LinkTags): String = taggedUrl(url, tags.parameters())

/**
 * Attach arbitrary [parameters] to [url], encoding each value.
 *
 * The [LinkTags] overload is what almost every caller wants; this exists for the store links,
 * whose attribution parameters are not UTMs.
 */
public fun taggedUrl(url: String, parameters: List<Pair<String, String>>): String {
    val query = parameters.toQueryString()
    if (query.isEmpty()) return url

    val fragmentStart = url.indexOf('#')
    val base = if (fragmentStart >= 0) url.substring(0, fragmentStart) else url
    val fragment = if (fragmentStart >= 0) url.substring(fragmentStart) else ""

    val separator = when {
        base.endsWith("?") || base.endsWith("&") -> ""
        base.contains('?') -> "&"
        else -> "?"
    }

    return "$base$separator$query$fragment"
}

/**
 * Mint a share id for `vf_s`.
 *
 * Short enough to survive being pasted into a text message, and drawn from an alphabet that needs
 * no encoding anywhere a URL goes. Sixty-two bits of entropy at the default length, which is far
 * more than the collision resistance a share id actually needs.
 */
public fun newShareId(length: Int = 11): String {
    val alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
    return buildString(length) {
        repeat(length) {
            append(alphabet[Random.nextInt(alphabet.length)])
        }
    }
}

private fun List<Pair<String, String>>.toQueryString(): String =
    joinToString("&") { (name, value) -> "$name=${encodeUrlComponent(value)}" }

/**
 * Percent-encode a query parameter value.
 *
 * Written out rather than delegated because this code compiles for both the JVM lambdas and the
 * browser, and the two platforms' built-in encoders disagree about spaces and about which
 * characters are reserved. Everything outside the unreserved set of RFC 3986 is escaped, so the
 * result is safe in a query string, inside another parameter, and in an email client's link parser
 * alike.
 */
internal fun encodeUrlComponent(value: String): String {
    val unreserved = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_.~"
    return buildString(value.length) {
        value.encodeToByteArray().forEach { byte ->
            val char = byte.toInt().toChar()
            if (byte >= 0 && unreserved.indexOf(char) >= 0) {
                append(char)
            } else {
                val unsigned = byte.toInt() and 0xFF
                append('%')
                append(HEX[unsigned shr 4])
                append(HEX[unsigned and 0x0F])
            }
        }
    }
}

private const val HEX = "0123456789ABCDEF"
